import { Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as St from './AdditionalDiscountLine.styles';

import { formatPriceUnitOrFree } from '~/core/utils';
import { useAppDispatch } from '~/redux/hooks';
import {
  resetDiscountOverride,
  updateDiscountOverride,
} from '~/redux/slices/estimate';

const DECIMAL_PATTERN = /^\d*\.?\d{0,2}$/;
const FINAL_DECIMAL_PATTERN = /^\d+(\.\d{1,2})?$/;

interface AdditionalDiscountLineProps {
  maximumDiscount: number;
  discountOverride: number;
}

const AdditionalDiscountLine = ({
  maximumDiscount,
  discountOverride,
}: AdditionalDiscountLineProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState<string>('');

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const enableDiscountEdit = () => {
    setLocalValue(discountOverride > 0 ? String(discountOverride) : '');
    setIsEditing(true);
  };

  const trackDiscountValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (DECIMAL_PATTERN.test(newValue)) {
      setLocalValue(newValue);
    }
  };

  const saveDiscountValue = () => {
    if (!localValue || !FINAL_DECIMAL_PATTERN.test(localValue)) {
      dispatch(resetDiscountOverride());
    } else {
      dispatch(
        updateDiscountOverride(Math.min(Number(localValue), maximumDiscount))
      );
    }
    setIsEditing(false);
  };

  const triggerBlur = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  if (isEditing) {
    return (
      <St.Container>
        <St.Label>{t('ORDER_SUMMARY.INVOICE.ADDITIONAL_DISCOUNT')}:</St.Label>
        <St.Input
          data-testid="discount-input"
          ref={inputRef}
          value={localValue}
          onChange={trackDiscountValue}
          onBlur={saveDiscountValue}
          onKeyDown={triggerBlur}
        />
      </St.Container>
    );
  }

  return (
    <Tooltip title={t('ORDER_SUMMARY.INVOICE.ADDITIONAL_DISCOUNT_TOOLTIP')}>
      <St.Container data-testid="discount-display" onClick={enableDiscountEdit}>
        <St.Label>{t('ORDER_SUMMARY.INVOICE.ADDITIONAL_DISCOUNT')}:</St.Label>
        <St.Value>{formatPriceUnitOrFree(discountOverride)}</St.Value>
      </St.Container>
    </Tooltip>
  );
};

export default AdditionalDiscountLine;
